import { useEffect, useState } from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";

const b64ToUint8 = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export function NotificationToggle() {
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [status, setStatus] = useState({ subscribed: false, devices: 0 });
  const [thisDevice, setThisDevice] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await apiClient.get("/push/status");
      setStatus(r.data);
      if (supported) {
        const reg = await navigator.serviceWorker.ready;
        setThisDevice(!!(await reg.pushManager.getSubscription()));
      }
    } catch {}
  };
  useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") throw new Error("Notifications were blocked — allow them in your browser settings");
      const reg = await navigator.serviceWorker.ready;
      const { data } = await apiClient.get("/push/public-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.public_key) });
      await apiClient.post("/push/subscribe", { subscription: sub.toJSON(), rain: true, outbreaks: true });
      await apiClient.post("/push/test");
      toast.success("Alerts on — we sent a test notification");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await apiClient.delete("/push/subscribe", { params: { endpoint: sub.endpoint } }); await sub.unsubscribe(); }
      toast.success("Alerts turned off on this device");
      await refresh();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div data-testid="notification-toggle" className="mb-8 bg-white rounded-2xl border border-stone-200 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className={`h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 ${thisDevice ? "bg-[#EAF6EF] text-[#2D6A4F]" : "bg-stone-100 text-stone-500"}`}>
        {thisDevice ? <BellRing className="h-6 w-6" /> : <Bell className="h-6 w-6" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-emerald-950">Outbreak &amp; heavy-rain alerts</div>
        <p className="text-sm text-stone-600" data-testid="notification-status">
          {!supported ? "Push notifications aren't supported in this browser. On iPhone, add AgroTerra to your home screen first."
            : thisDevice ? `On for this device${status.devices > 1 ? ` · ${status.devices} devices total` : ""}. We check every 6 hours, even when the app is closed.`
            : status.subscribed ? `On for ${status.devices} other device${status.devices > 1 ? "s" : ""} — turn on here too?`
            : "Get a notification when a pest you grow is spotted nearby or heavy rain is coming to a field."}
        </p>
      </div>
      {supported && (thisDevice
        ? <Button data-testid="notifications-off-btn" onClick={disable} disabled={busy} variant="outline" className="border-stone-300 rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><BellOff className="h-4 w-4 mr-2" /> Turn off</>}</Button>
        : <Button data-testid="notifications-on-btn" onClick={enable} disabled={busy} className="bg-[#2D6A4F] hover:bg-[#143D29] text-white rounded-full px-5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Bell className="h-4 w-4 mr-2" /> Turn on alerts</>}</Button>)}
    </div>
  );
}
